import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useT } from '../i18n';
import { estimateProject, round2, computeTotalFromBreakdown, CURRENCIES, currencySymbol } from '../utils/costEstimate';
import { setProjectPrice } from '../firebase/workspaceService';
import './CostCalculator.css';

const num = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };

export default function CostCalculator({ project, onClose, onSaved }) {
  const { t } = useT();
  const est = useMemo(() => estimateProject(project?.data), [project?.data]);

  // Translate an item's display name the same way the 3D views do:
  // t('furn_<type>') / t('light_<type>'), falling back to the stored English
  // label. Dynamic so it works for any item type without hard-coding a list.
  const itemName = (it) => {
    const key = (it.kind === 'lighting' ? 'light_' : 'furn_') + it.type;
    const tr = t(key);
    return (tr && tr !== key) ? tr : (it.label || it.type);
  };

  // Reopen with the previously-saved breakdown (rates + per-item prices) so the
  // user can review/edit rather than starting over. Stored as JSON in priceData.
  const saved = useMemo(() => {
    try { return project?.priceData ? JSON.parse(project.priceData) : {}; }
    catch { return {}; }
  }, [project?.priceData]);

  const [currency, setCurrency] = useState(saved.currency || project?.amount || 'USD');
  const [floorRate, setFloorRate] = useState(saved.floorRate ?? '');
  const [wallRate,  setWallRate]  = useState(saved.wallRate ?? '');
  const [ceilRate,  setCeilRate]  = useState(saved.ceilRate ?? '');
  const [itemPrices, setItemPrices] = useState(saved.itemPrices || {}); // uid → unit price string
  const [busy, setBusy] = useState(false);
  const [curOpen, setCurOpen] = useState(false); // currency dropdown open
  const curRef = useRef(null);

  // Close the currency dropdown on outside click / Escape.
  useEffect(() => {
    if (!curOpen) return;
    const onDown = (e) => { if (curRef.current && !curRef.current.contains(e.target)) setCurOpen(false); };
    const onKey = (e) => { if (e.key === 'Escape') setCurOpen(false); };
    document.addEventListener('mousedown', onDown);
    document.addEventListener('keydown', onKey);
    return () => { document.removeEventListener('mousedown', onDown); document.removeEventListener('keydown', onKey); };
  }, [curOpen]);

  const sym = currencySymbol(currency);
  const curOpt = CURRENCIES.find(c => c.code === currency) || CURRENCIES[0];

  const floorCost = round2(est.totalFloor   * num(floorRate));
  const wallCost  = round2(est.totalWall    * num(wallRate));
  const ceilCost  = round2(est.totalCeiling * num(ceilRate));
  const itemsCost = round2(est.items.reduce((s, it) => s + it.count * num(itemPrices[it.uid]), 0));
  const grand     = computeTotalFromBreakdown(est, { floorRate, wallRate, ceilRate, itemPrices });

  async function handleCalculate() {
    setBusy(true);
    // Persist the full breakdown so it can be reopened and edited later.
    const priceData = JSON.stringify({ currency, floorRate, wallRate, ceilRate, itemPrices, grand });
    try {
      await setProjectPrice(project.id, grand, currency, priceData);
      onSaved?.(grand, currency, priceData);
      onClose();
    } catch (err) {
      console.error('[Cost] save failed:', err);
    } finally {
      setBusy(false);
    }
  }

  const money = (n) => `${sym}${n.toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

  return (
    <div className="cc-overlay" onClick={e => { if (e.target === e.currentTarget && !busy) onClose(); }}>
      <div className="cc-modal" role="dialog" aria-modal="true">
        <div className="cc-head">
          <h3 className="cc-title">{t('cc_title')}</h3>
          <button className="cc-close" onClick={onClose} disabled={busy} aria-label="Close">
            <svg viewBox="0 0 24 24" fill="none" width="18" height="18" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round">
              <line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/>
            </svg>
          </button>
        </div>

        <div className="cc-body">
          {/* Currency dropdown */}
          <div className="cc-currency" ref={curRef}>
            <span className="cc-cur-caption">{t('cc_currency')}</span>
            <button
              type="button"
              className={`cc-cur-trigger${curOpen ? ' cc-cur-trigger--open' : ''}`}
              onClick={() => setCurOpen(o => !o)}
              aria-haspopup="listbox"
              aria-expanded={curOpen}
            >
              <span className="cc-cur-sym">{curOpt.symbol}</span>
              <span className="cc-cur-name">{t('cc_cur_' + curOpt.code)}</span>
              <svg className="cc-cur-chevron" viewBox="0 0 24 24" width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="6 9 12 15 18 9"/>
              </svg>
            </button>
            {curOpen && (
              <div className="cc-cur-menu" role="listbox">
                {CURRENCIES.map(c => (
                  <button
                    key={c.code}
                    role="option"
                    aria-selected={currency === c.code}
                    className={`cc-cur-opt${currency === c.code ? ' cc-cur-opt--active' : ''}`}
                    onClick={() => { setCurrency(c.code); setCurOpen(false); }}
                  >
                    <span className="cc-cur-sym">{c.symbol}</span>
                    <span className="cc-cur-name">{t('cc_cur_' + c.code)}</span>
                    {currency === c.code && (
                      <svg className="cc-cur-check" viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                    )}
                  </button>
                ))}
              </div>
            )}
          </div>

          {est.rooms.length === 0 ? (
            <p className="cc-empty">{t('cc_empty')}</p>
          ) : (
            <>
              {/* Surfaces per room */}
              <div className="cc-section-title">{t('cc_surfaces')}</div>
              <div className="cc-table-wrap">
                <table className="cc-table">
                  <thead>
                    <tr>
                      <th>{t('cc_room')}</th>
                      <th className="cc-num">{t('cc_floor')} m²</th>
                      <th className="cc-num">{t('cc_wall')} m²</th>
                      <th className="cc-num">{t('cc_ceiling')} m²</th>
                    </tr>
                  </thead>
                  <tbody>
                    {est.rooms.map(r => (
                      <tr key={r.id}>
                        <td>{r.label}</td>
                        <td className="cc-num">{r.floorSqm}</td>
                        <td className="cc-num">{r.wallSqm}</td>
                        <td className="cc-num">{r.ceilingSqm}</td>
                      </tr>
                    ))}
                    {/* Free walls belong to no room, so they'd otherwise be
                        invisible here even though they're in the wall total. */}
                    {est.freeWallSqm > 0 && (
                      <tr>
                        <td>{t('cc_free_walls')}</td>
                        <td className="cc-num">—</td>
                        <td className="cc-num">{est.freeWallSqm}</td>
                        <td className="cc-num">—</td>
                      </tr>
                    )}
                    <tr className="cc-total-row">
                      <td>{t('cc_total')}</td>
                      <td className="cc-num">{est.totalFloor}</td>
                      <td className="cc-num">{est.totalWall}</td>
                      <td className="cc-num">{est.totalCeiling}</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              {/* Rate inputs per surface */}
              <div className="cc-rates">
                {[
                  { label: t('cc_floor'),   total: est.totalFloor,   rate: floorRate, set: setFloorRate, cost: floorCost },
                  { label: t('cc_wall'),    total: est.totalWall,    rate: wallRate,  set: setWallRate,  cost: wallCost },
                  { label: t('cc_ceiling'), total: est.totalCeiling, rate: ceilRate,  set: setCeilRate,  cost: ceilCost },
                ].map((row, i) => (
                  <div className="cc-rate-row" key={i}>
                    <span className="cc-rate-label">{row.label}</span>
                    <span className="cc-rate-area">{row.total} m²</span>
                    <span className="cc-rate-x">×</span>
                    <div className="cc-input-wrap">
                      <span className="cc-input-sym">{sym}</span>
                      <input
                        className="cc-input" type="number" min="0" inputMode="decimal"
                        placeholder={t('cc_per_sqm')}
                        value={row.rate} onChange={e => row.set(e.target.value)}
                      />
                    </div>
                    <span className="cc-rate-cost">{money(row.cost)}</span>
                  </div>
                ))}
              </div>

              {/* Items */}
              {est.items.length > 0 && (
                <>
                  <div className="cc-section-title">{t('cc_items')}</div>
                  <div className="cc-table-wrap">
                    <table className="cc-table">
                      <thead>
                        <tr>
                          <th>{t('cc_room')}</th>
                          <th>{t('cc_item')}</th>
                          <th className="cc-num">{t('cc_qty')}</th>
                          <th className="cc-num">{t('cc_unit_price')}</th>
                          <th className="cc-num">{t('cc_total')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {est.items.map(it => {
                          const line = round2(it.count * num(itemPrices[it.uid]));
                          return (
                            <tr key={it.uid}>
                              <td className="cc-muted">{it.roomLabel}</td>
                              <td>{itemName(it)}</td>
                              <td className="cc-num">{it.count}</td>
                              <td className="cc-num">
                                <div className="cc-input-wrap cc-input-wrap--sm">
                                  <span className="cc-input-sym">{sym}</span>
                                  <input
                                    className="cc-input" type="number" min="0" inputMode="decimal"
                                    value={itemPrices[it.uid] || ''}
                                    onChange={e => setItemPrices(p => ({ ...p, [it.uid]: e.target.value }))}
                                  />
                                </div>
                              </td>
                              <td className="cc-num">{money(line)}</td>
                            </tr>
                          );
                        })}
                        <tr className="cc-total-row">
                          <td colSpan={4}>{t('cc_items_total')}</td>
                          <td className="cc-num">{money(itemsCost)}</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>
                </>
              )}
            </>
          )}
        </div>

        <div className="cc-foot">
          <div className="cc-grand">
            <span className="cc-grand-label">{t('cc_grand_total')}</span>
            <span className="cc-grand-value">{money(grand)}</span>
          </div>
          <button className="cc-calc-btn" onClick={handleCalculate} disabled={busy || est.rooms.length === 0}>
            {busy ? <span className="cc-spinner" /> : null}
            {t('cc_calculate')}
          </button>
        </div>
      </div>
    </div>
  );
}
